import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import apiClient from "../../services/apiClient";
import { normalizeApiError } from "../../utils/apiError";
import Button from "../../components/common/Button";
import CrudForm from "../../components/common/crud/CrudForm";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";
import RolePermissionsModal from "../../components/roles/RolePermissionsModal";
import alertService from "../../utils/alertService";

const roleFormSchema = [
  {
    name: "roleName",
    label: "Role Name",
    type: "text",
    placeholder: "Administrator",
    validation: {
      required: true,
      minLength: 3,
      maxLength: 50,
    },
  },
  {
    name: "isActive",
    label: "Is Active",
    type: "checkbox",
    defaultValue: true,
  },
];

function RolesModulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useEncryptedParams();
  const [editValues, setEditValues] = useState(null);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [formError, setFormError] = useState(null);
  const [permissionsTarget, setPermissionsTarget] = useState(null);

  const isCreatePage = location.pathname.endsWith("/new");
  const isEditPage = Boolean(id) && (location.pathname.endsWith("/edit") || location.pathname.includes(`/${id}`));
  const isFormPage = isCreatePage || isEditPage;
  const formMode = isEditPage ? "edit" : "create";

  const basePath = useMemo(() => {
    if (location.pathname.includes("/user-master/roles")) return "/user-master/roles";
    return "/roles";
  }, [location.pathname]);

  const endpoints = useMemo(
    () => ({
      list: "/roles",
      create: "/roles",
      edit: (id) => `/roles/${id}`,
      update: (id) => `/roles/${id}`,
      delete: (id) => `/roles/${id}`,
    }),
    []
  );

  const {
    rows,
    isLoading,
    isSubmitting,
    error,
    filters,
    pagination,
    sortConfig,
    askDelete,
    refresh,
    updateFilters,
    changeSort,
    changePage,
  } = useCrud({
    endpoints,
    idKey: "id",
    updateMethod: "patch",
    mapEditItem: (payload) => payload?.data || payload || {},
  });

  const roleTableColumns = useMemo(
    () => [
      { key: "roleName", label: "Role Name", sortable: false },
      {
        key: "status",
        label: "Status",
        render: (item) => (
          <span
            className={`rounded-full px-2 py-1 text-xs font-semibold ${
              item?.isActive
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
            }`}
          >
            {item?.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    if (!isEditPage || !id) {
      setEditValues(null);
      setFormError(null);
      return;
    }

    let isMounted = true;
    const loadEditRole = async () => {
      setIsLoadingEdit(true);
      setFormError(null);
      try {
        const response = await apiClient.get(`/roles/${id}`);
        const payload = response?.data || {};
        const role = payload?.data || payload || {};
        if (!isMounted) return;
        setEditValues(role);
      } catch (err) {
        if (!isMounted) return;
        const normalized = normalizeApiError(err);
        setFormError(normalized.message);
      } finally {
        if (isMounted) setIsLoadingEdit(false);
      }
    };

    loadEditRole();
    return () => {
      isMounted = false;
    };
  }, [id, isEditPage]);

  const saveRole = async (values) => {
    setFormError(null);
    try {
      if (formMode === "edit" && id) {
        await apiClient.patch(`/roles/${id}`, values);
        alertService.success("Record updated successfully.");
      } else {
        await apiClient.post("/roles", values);
        alertService.success("Record created successfully.");
      }
      await refresh();
      navigate(basePath, { replace: true });
    } catch (err) {
      const normalized = normalizeApiError(err);
      setFormError(normalized.message);
      alertService.error(normalized.message);
    }
  };

  const openCreate = () => navigate(`${basePath}/new`);
  const openEdit = (item) => navigate(`${basePath}/${encrypt(item?.id || item?.roleId)}/edit`);

  if (isFormPage) {
    const notFound = formMode === "edit" && !isLoadingEdit && !editValues && !formError;

    return (
      <div className="space-y-3">
        <div className="flex justify-end">
          <Button variant="secondary" onClick={() => navigate(basePath)}>
            Back to List
          </Button>
        </div>

        {formError ? <ErrorAlert message={formError} /> : null}
        {notFound ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">Record not found.</div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <CrudForm
              mode={formMode}
              schema={roleFormSchema}
              initialValues={formMode === "edit" ? editValues || {} : {}}
              isSubmitting={isSubmitting || isLoadingEdit}
              onSubmit={saveRole}
              onCancel={() => navigate(basePath)}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error ? <ErrorAlert message={error} /> : null}

      <CrudTable
        columns={roleTableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        onEdit={openEdit}
        onDelete={askDelete}
        renderActions={(item) => (
          <Button
            variant="secondary"
            className="px-2.5 py-1 text-xs border-cyan-200 text-cyan-700 hover:bg-cyan-50 dark:border-cyan-500/30 dark:text-cyan-400 dark:hover:bg-cyan-500/10"
            onClick={() => setPermissionsTarget(item)}
          >
            <div className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              <span>Permissions</span>
            </div>
          </Button>
        )}
        searchable={false}
        sortable={false}
        paginated={false}
        tableScrollHeight="52vh"
        toolbarAction={<Button onClick={openCreate}>Create Role</Button>}
      />

      <RolePermissionsModal
        isOpen={Boolean(permissionsTarget)}
        onClose={() => setPermissionsTarget(null)}
        role={permissionsTarget}
      />
    </div>
  );
}

export default RolesModulePage;
